import {
  calendarDaysBetween,
  employmentAnniversary,
  parseIsoDate,
  yearToDateEmploymentDays,
  daysInYear,
} from "./dates";
import {
  decimal,
  maxDecimal,
  minDecimal,
  money,
  nonNegative,
} from "./money";
import {
  LEGAL_SOURCES,
  fiscalSourcesFor,
  parametersFor,
  statutoryVacationDays,
} from "./parameters";
import {
  calculateSocialSecurity,
  dailySalary,
  integrationFactor,
} from "./payroll";
import {
  calculatePeriodIncomeTax,
  calculateSeparationWithholding,
} from "./tax";
import type {
  CalculationResult,
  LitigationInput,
  ResultLine,
  SeparationInput,
} from "./types";

function shouldPaySeniorityPremium(
  input: SeparationInput | LitigationInput,
  completedYears: number,
): boolean {
  if (typeof input.includeSeniorityPremium === "boolean") {
    return input.includeSeniorityPremium;
  }
  if (input.reason === "resignation" || input.reason === "mutual") {
    return completedYears >= 15;
  }
  return true;
}

function fiscalServiceYears(serviceDays: number): number {
  const complete = Math.floor(serviceDays / 365);
  const remainder = serviceDays - complete * 365;
  return complete + (remainder > 182.5 ? 1 : 0);
}

function separationTitle(kind: SeparationInput["kind"]): string {
  return kind === "finiquito"
    ? "Estimación de finiquito"
    : "Estimación de liquidación";
}

export function calculateSeparation(
  input: SeparationInput,
): CalculationResult {
  const params = parametersFor(input.payDate);
  parseIsoDate(input.startDate);
  parseIsoDate(input.endDate);
  if (input.endDate < input.startDate) {
    throw new Error("La fecha de baja no puede ser anterior al ingreso.");
  }
  if (input.payDate < input.endDate) {
    throw new Error("La fecha de pago no puede ser anterior a la baja.");
  }

  const daily = dailySalary(input.wage);
  if (daily <= 0) throw new Error("El salario debe ser mayor a cero.");
  const anniversary = employmentAnniversary(input.startDate, input.endDate);
  const serviceYears = anniversary.serviceDays / 365;
  const aguinaldoDays = Math.max(15, input.aguinaldoDays ?? 15);
  const vacationPremiumRate = Math.max(
    0.25,
    input.vacationPremiumRate ?? 0.25,
  );
  const annualVacationDays = Math.max(
    statutoryVacationDays(anniversary.completedYears),
    input.vacationDaysPerYear ??
      statutoryVacationDays(anniversary.completedYears),
  );
  const factor = integrationFactor(
    aguinaldoDays,
    annualVacationDays,
    vacationPremiumRate,
  );
  const integratedDaily = money(decimal(daily).times(factor));
  const sbcDaily = money(
    minDecimal(
      maxDecimal(integratedDaily, params.minimumWage[input.zone]),
      decimal(params.umaDaily).times(25),
    ),
  );

  const unpaidSalaryDays = Math.max(0, input.unpaidSalaryDays ?? 0);
  const unpaidSalary = money(decimal(daily).times(unpaidSalaryDays));
  const yearDays = daysInYear(parseIsoDate(input.endDate).getUTCFullYear());
  const aguinaldoWorkedDays = yearToDateEmploymentDays(
    input.startDate,
    input.endDate,
  );
  const aguinaldo = money(
    decimal(daily)
      .times(aguinaldoDays)
      .times(aguinaldoWorkedDays)
      .div(yearDays),
  );
  const aguinaldoExempt = money(
    minDecimal(aguinaldo, decimal(params.umaDaily).times(30)),
  );

  const proportionalVacationDays = money(
    decimal(annualVacationDays)
      .times(anniversary.currentPeriodDays)
      .div(365),
  );
  const totalVacationDays = money(
    decimal(proportionalVacationDays).plus(
      nonNegative(input.unusedVacationDays),
    ),
  );
  const vacationPay = money(decimal(daily).times(totalVacationDays));
  const vacationPremium = money(
    decimal(vacationPay).times(vacationPremiumRate),
  );
  const vacationPremiumExempt = money(
    minDecimal(vacationPremium, decimal(params.umaDaily).times(15)),
  );
  const otherOrdinary = money(nonNegative(input.otherOrdinaryPayments));

  const liquidation =
    input.kind === "liquidation" &&
    (input.reason === "unjustified-dismissal" ||
      input.reason === "employee-rescission");
  const includeThreeMonths =
    liquidation && (input.includeThreeMonths ?? true);
  const includeTwentyDays =
    liquidation && (input.includeTwentyDaysPerYear ?? false);
  const includeSeniority = shouldPaySeniorityPremium(
    input,
    anniversary.completedYears,
  );

  const threeMonths = includeThreeMonths
    ? money(decimal(integratedDaily).times(90))
    : 0;
  const twentyDays = includeTwentyDays
    ? money(decimal(integratedDaily).times(20).times(serviceYears))
    : 0;
  const seniorityPremiumDaily = Math.min(
    daily,
    params.minimumWage[input.zone] * 2,
  );
  const seniorityPremium = includeSeniority
    ? money(decimal(seniorityPremiumDaily).times(12).times(serviceYears))
    : 0;
  const separationGross = money(
    decimal(threeMonths).plus(twentyDays).plus(seniorityPremium),
  );
  const exemptionYears = fiscalServiceYears(anniversary.serviceDays);
  const separationExempt = money(
    minDecimal(
      separationGross,
      decimal(params.umaDaily).times(90).times(exemptionYears),
    ),
  );
  const taxableSeparation = money(
    decimal(separationGross).minus(separationExempt),
  );

  const ordinaryGross = money(
    decimal(unpaidSalary)
      .plus(aguinaldo)
      .plus(vacationPay)
      .plus(vacationPremium)
      .plus(otherOrdinary),
  );
  const ordinaryExempt = money(
    decimal(aguinaldoExempt).plus(vacationPremiumExempt),
  );
  const ordinaryTaxable = money(decimal(ordinaryGross).minus(ordinaryExempt));
  const ordinaryTax = calculatePeriodIncomeTax(
    ordinaryTaxable,
    Math.max(1, unpaidSalaryDays || 30.4),
    params,
    {
      monthly: unpaidSalaryDays === 0,
      subsidyEligible: separationGross === 0,
    },
  );
  const lastMonthlySalary = money(decimal(daily).times(30));
  const separationTax = calculateSeparationWithholding(
    taxableSeparation,
    lastMonthlySalary,
    params,
  );
  const socialSecurity =
    unpaidSalaryDays > 0
      ? calculateSocialSecurity(
          sbcDaily,
          unpaidSalaryDays,
          params.minimumWage[input.zone],
          params.umaDaily,
          0.54355,
          params.ceavEmployerRates,
          daily <= params.minimumWage[input.zone],
        ).employee
      : 0;

  const gross = money(decimal(ordinaryGross).plus(separationGross));
  const exempt = money(decimal(ordinaryExempt).plus(separationExempt));
  const taxable = money(decimal(gross).minus(exempt));
  const isr = money(
    decimal(ordinaryTax.isrWithheld).plus(separationTax.withholding),
  );
  const net = money(decimal(gross).minus(isr).minus(socialSecurity));

  const lines: ResultLine[] = [
    ...(unpaidSalary
      ? [
          {
            key: "unpaid-salary",
            label: `Salario pendiente (${unpaidSalaryDays} días)`,
            amount: unpaidSalary,
            category: "earning" as const,
          },
        ]
      : []),
    {
      key: "aguinaldo",
      label: "Aguinaldo proporcional",
      amount: aguinaldo,
      category: "benefit",
      legalBasis: "LFT art. 87",
      note: `${aguinaldoWorkedDays} días computados en ${parseIsoDate(input.endDate).getUTCFullYear()}.`,
    },
    {
      key: "vacation",
      label: `Vacaciones (${totalVacationDays.toFixed(2)} días)`,
      amount: vacationPay,
      category: "benefit",
      legalBasis: "LFT arts. 76 y 79",
      note: `${proportionalVacationDays.toFixed(2)} proporcionales desde el aniversario ${anniversary.lastAnniversary}.`,
    },
    {
      key: "vacation-premium",
      label: `Prima vacacional (${(vacationPremiumRate * 100).toFixed(2)}%)`,
      amount: vacationPremium,
      category: "benefit",
      legalBasis: "LFT art. 80",
    },
    ...(otherOrdinary
      ? [
          {
            key: "other-ordinary",
            label: "Otros pagos ordinarios capturados",
            amount: otherOrdinary,
            category: "earning" as const,
          },
        ]
      : []),
    ...(threeMonths
      ? [
          {
            key: "three-months",
            label: "Indemnización constitucional de tres meses",
            amount: threeMonths,
            category: "indemnity" as const,
            legalBasis: "LFT arts. 48, 50 y 89",
          },
        ]
      : []),
    ...(twentyDays
      ? [
          {
            key: "twenty-days",
            label: "Veinte días por año",
            amount: twentyDays,
            category: "indemnity" as const,
            legalBasis: "LFT art. 50",
            note:
              "Se incluye porque fue seleccionado; no procede automáticamente en todo despido.",
          },
        ]
      : []),
    ...(seniorityPremium
      ? [
          {
            key: "seniority",
            label: "Prima de antigüedad",
            amount: seniorityPremium,
            category: "indemnity" as const,
            legalBasis: "LFT arts. 162, 485 y 486",
            note: `Base diaria topada en $${seniorityPremiumDaily.toFixed(2)}.`,
          },
        ]
      : []),
    {
      key: "isr",
      label: "ISR provisional estimado",
      amount: -isr,
      category: "tax",
      legalBasis: "LISR arts. 93-XIII, 95 y 96",
      note:
        separationGross > 0
          ? `La parte por separación usa una tasa efectiva de ${(separationTax.effectiveRate * 100).toFixed(2)}%.`
          : undefined,
    },
    ...(socialSecurity
      ? [
          {
            key: "imss",
            label: "Cuotas obreras IMSS sobre salario pendiente",
            amount: -socialSecurity,
            category: "deduction" as const,
          },
        ]
      : []),
  ];

  const warnings = [
    "El ISR mostrado es una retención provisional. El impuesto anual puede cambiar por otros ingresos, deducciones, empleadores y retenciones del ejercicio.",
    "Las vacaciones pendientes de años completos deben capturarse manualmente; la herramienta calcula en automático sólo la parte proporcional del periodo de servicio en curso.",
    ...(input.kind === "finiquito" &&
    (input.reason === "unjustified-dismissal" ||
      input.reason === "employee-rescission")
      ? [
          "Elegiste un supuesto que puede generar indemnización, pero estás viendo sólo finiquito. Revisa también la calculadora de liquidación.",
        ]
      : []),
    ...(input.kind === "liquidation" && !includeTwentyDays
      ? [
          "No se incluyeron veinte días por año. Su procedencia depende de la acción, tipo de relación y supuesto jurídico; no es automática en todo despido injustificado.",
        ]
      : []),
    ...(input.reason === "mutual"
      ? [
          "En una terminación por mutuo consentimiento, cualquier gratificación adicional depende del convenio y no se presume.",
        ]
      : []),
  ];

  return {
    schemaVersion: 1,
    rulesVersion: params.version,
    calculatedAt: new Date().toISOString(),
    kind: input.kind,
    title: separationTitle(input.kind),
    salary: {
      daily,
      integratedDaily,
      contributionBaseDaily: sbcDaily,
    },
    totals: {
      gross,
      exempt,
      taxable,
      isr,
      socialSecurity,
      otherDeductions: 0,
      net,
    },
    lines,
    warnings,
    assumptions: [
      `Antigüedad: ${serviceYears.toFixed(4)} años (${anniversary.serviceDays} días).`,
      `Exención por separación: ${exemptionYears} año(s) fiscal(es), conforme a la regla de fracción mayor a seis meses.`,
      `Prestaciones mínimas usadas: ${aguinaldoDays} días de aguinaldo, ${annualVacationDays} días de vacaciones y ${(vacationPremiumRate * 100).toFixed(2)}% de prima.`,
      "Se asume relación por tiempo indeterminado y salario fijo. En modo avanzado deben ajustarse prestaciones superiores y conceptos pendientes.",
    ],
    citations: [
      LEGAL_SOURCES.lft,
      LEGAL_SOURCES.lisr,
      ...fiscalSourcesFor(input.payDate),
      LEGAL_SOURCES.uma,
      LEGAL_SOURCES.minimumWage,
    ],
  };
}

export function calculateLitigation(
  input: LitigationInput,
): CalculationResult {
  if (input.valuationDate < input.endDate) {
    throw new Error("La fecha de valuación no puede ser anterior al despido.");
  }
  const base = calculateSeparation({
    ...input,
    kind: "liquidation",
  });
  const params = parametersFor(input.payDate);
  const elapsedDays = calendarDaysBetween(
    input.endDate,
    input.valuationDate,
    false,
  );
  const backPayDays = Math.min(elapsedDays, 365);
  const backPay = money(
    decimal(base.salary.integratedDaily).times(backPayDays),
  );
  const interestMonths = Math.max(0, (elapsedDays - 365) / 30.4);
  const interest = money(
    decimal(base.salary.integratedDaily)
      .times(30)
      .times(15)
      .times(0.02)
      .times(interestMonths),
  );
  const litigationGross = money(decimal(backPay).plus(interest));
  const monthlyIntegrated = money(
    decimal(base.salary.integratedDaily).times(30),
  );
  const fullMonths = Math.floor(backPayDays / 30);
  const remainingDays = backPayDays - fullMonths * 30;
  const monthlyTax = calculatePeriodIncomeTax(
    monthlyIntegrated,
    30.4,
    params,
    { monthly: true, subsidyEligible: false },
  ).taxBeforeSubsidy;
  const remainderTax =
    remainingDays > 0
      ? calculatePeriodIncomeTax(
          money(decimal(base.salary.integratedDaily).times(remainingDays)),
          remainingDays,
          params,
          { subsidyEligible: false },
        ).taxBeforeSubsidy
      : 0;
  const interestTax = calculateSeparationWithholding(
    interest,
    money(decimal(base.salary.daily).times(30)),
    params,
  ).withholding;
  const litigationTax = money(
    decimal(monthlyTax)
      .times(fullMonths)
      .plus(remainderTax)
      .plus(interestTax),
  );

  return {
    ...base,
    kind: "litigation",
    title: "Escenario litigioso por despido",
    totals: {
      ...base.totals,
      gross: money(decimal(base.totals.gross).plus(litigationGross)),
      taxable: money(decimal(base.totals.taxable).plus(litigationGross)),
      isr: money(decimal(base.totals.isr).plus(litigationTax)),
      net: money(
        decimal(base.totals.net).plus(litigationGross).minus(litigationTax),
      ),
    },
    lines: [
      ...base.lines,
      {
        key: "back-pay",
        label: `Salarios vencidos (${backPayDays} días)`,
        amount: backPay,
        category: "litigation",
        legalBasis: "LFT art. 48",
        note: "Topados a doce meses.",
      },
      ...(interest
        ? [
            {
              key: "litigation-interest",
              label: `Intereses posteriores (${interestMonths.toFixed(2)} meses)`,
              amount: interest,
              category: "litigation" as const,
              legalBasis: "LFT art. 48",
              note:
                "2% mensual sobre quince meses de salario; capitalización al pago.",
            },
          ]
        : []),
      {
        key: "litigation-tax",
        label: "ISR adicional estimado del escenario litigioso",
        amount: -litigationTax,
        category: "tax",
        note:
          "La retención real depende de la forma y fecha de pago, conceptos de la sentencia y situación anual.",
      },
    ],
    warnings: [
      "Este escenario es contingente: no predice el sentido de una sentencia, la duración real del juicio, pruebas, defensas, ofrecimiento de reinstalación ni la solvencia del patrón.",
      "No se suman por separado prestaciones que pudieran ya integrar el salario usado para salarios vencidos; un abogado debe evitar duplicidades según las acciones ejercidas.",
      "La conciliación prejudicial, interrupción de prescripción y días hábiles requieren revisar constancias y fechas del expediente.",
      ...base.warnings,
    ],
    assumptions: [
      `Valuación al ${input.valuationDate}: ${elapsedDays} días desde el despido.`,
      "Los salarios vencidos se estiman con salario diario integrado y se topan a doce meses.",
      "Los intereses se modelan de forma simple al 2% mensual sobre quince meses de salario y se capitalizan al pago.",
      ...base.assumptions,
    ],
    citations: Array.from(
      new Set([...base.citations, LEGAL_SOURCES.lft, LEGAL_SOURCES.lisr]),
    ),
  };
}
